import { useTracking } from '@folklore/tracking';
import classNames from 'classnames';
import { useEffect, useRef, useState } from 'react';

import { useAccess, useHasAccess } from '../../hooks/useAccess';
import { useDocumentProgress } from '../../hooks/useDocument';
import { useContinueDocuments, useNextDocuments } from '../../hooks/useDocuments';
import usePlaceholderItems from '../../hooks/usePlaceholderItems';
import { useDocumentSurtitle } from '../../hooks/useSurtitle';
import { formatDuration } from '../../lib/utils';

import { useDefaultBrand } from '../../contexts/SiteContext';
import BasicButton from '../buttons/BasicButton';
import PlayButton from '../buttons/PlayButton';
import DocumentsList from '../lists/DocumentsList';
import Blocked from '../partials/Blocked';
import Duration from '../partials/Duration';
import Picture from '../partials/Picture';
import Surtitle from '../partials/Surtitle';
import Video from '../partials/Video';
import ListTitle from '../typography/ListTitle';
import SummaryTitleTypography from '../typography/SummaryTitle';

import styles from '../../styles/documents/video-document.module.css';

export interface VideoDocumentProps extends Partial<VideoLikeDocument> {
    paused?: boolean;
    autoPlay?: boolean;
    controls?: boolean;
    current?: boolean;
    entered?: boolean;
    entering?: boolean;
    leaving?: boolean;
    transitioning?: boolean;
    onEnter?: (() => void) | null;
    onLeave?: (() => void) | null;
    onComplete?: (() => void) | null;
    onProgress?: ((progress: number) => void) | null;
    className?: string | null;
}

function VideoDocument({
    id = null,
    type = null,
    surtitle = null,
    title = null,
    video = null,
    trailer = null,
    image = null,
    metadata = null,
    access = null,
    paused = false,
    autoPlay = false,
    controls = true,
    current = false,
    entered = false,
    entering = false,
    leaving = false,
    transitioning= false,
    onEnter = null,
    onLeave = null,
    onComplete = null,
    onProgress = null,
    className = null,
}: VideoDocumentProps) {
    const [playing, setPlaying] = useState(autoPlay);
    const [hasAutoPlayed, setHasAutoPlayed] = useState(false);
    const [muted, setMuted] = useState(false);
    const [ended, setEnded] = useState(false);
    const [hasPlayed, setHasPlayed] = useState(false);
    const [skipStartTime, setSkipStartTime] = useState(false);
    const { brands, duration: metadataDuration, collections } = metadata || {};
    const [brand = null] = brands || [];
    const [collection = null] = collections || [];
    const { handle: brandHandle } = brand || {};
    const { handle: defaultBrandHandle = 'urbania' } = useDefaultBrand();
    const playerRef = useRef(null);

    const finalVideo = video || trailer;
    const isTrailer = trailer !== null && video === null;
    const finalAccess = useAccess({
        collection,
        access,
    });
    const { blocked = false } = finalAccess || {};
    const unblocked = useHasAccess(finalAccess);

    const { duration: videoDuration } = finalVideo || {};
    const duration = videoDuration || metadataDuration;

    const finalSurtitle = useDocumentSurtitle({
        type,
        surtitle,
        metadata,
    });

    const timeoutRef = useRef(null);
    const startLeaveTimeout = () => {
        if (onLeave === null || !current) {
            return;
        }
        timeoutRef.current = setTimeout(() => {
            timeoutRef.current = null;
            onLeave();
        }, 1000);
    };
    const stopLeaveTimeout = () => {
        if (timeoutRef.current !== null) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
    };

    const onPlay = () => {
        setEnded(false);
        setPlaying(true);
        setHasPlayed(true);
        if (autoPlay) {
            setHasAutoPlayed(true);
        }
        if (onEnter !== null && current) {
            onEnter();
        }
    };

    const { progress } = useDocumentProgress(id);
    const startTime = progress !== null ? duration * progress : null;
    useEffect(() => {
        if (hasPlayed && !skipStartTime && current) {
            if (playerRef.current !== null && startTime !== null && startTime > 0) {
                playerRef.current.seek(startTime);
            }
        }
    }, [current, hasPlayed, skipStartTime]);

    const onPause = () => {
        setPlaying(false);
        startLeaveTimeout();
    };

    const onMute = () => {
        setMuted(true);
    };

    const onUnmute = () => {
        setMuted(false);
    };

    const onEnded = () => {
        setEnded(true);
        startLeaveTimeout();
        if (onComplete !== null) {
            onComplete();
        }
    };

    const onBufferStart = () => {
        stopLeaveTimeout();
    };

    const onBufferEnd = () => {
        stopLeaveTimeout();
    };

    const onClickVideo = (newPlaying: boolean) => {
        if (!current) {
            return;
        }
        if (newPlaying && onEnter !== null) {
            onEnter();
        }
    };

    const tracking = useTracking();
    const [willPlay, setWillPlay] = useState(false);
    const [startTimeChoiceOpened, setStartTimeChoiceOpened] = useState(false);
    const onClickPlay = () => {
        if (!current) {
            return;
        }
        if (startTime !== null && startTime > 10 && !startTimeChoiceOpened && !isTrailer) {
            setStartTimeChoiceOpened(true);
            return;
        }
        setPlaying(true);
        setWillPlay(true);
        if (startTime !== null) {
            tracking.trackEvent('Video', 'click_resume', startTime);
        }
    };
    const onClickPlayStart = () => {
        if (!current) {
            return;
        }
        setPlaying(true);
        setWillPlay(true);
        setSkipStartTime(true);
        tracking.trackEvent('Video', 'click_start_beginning');
    };

    // useEffect(() => {
    //     if (autoPlay && current) {
    //         playerRef.current?.play();
    //     }
    // }, [autoPlay, current]);

    const [hasBeenCurrent, setHasBeenCurrent] = useState(current);
    if (current && !transitioning && !hasBeenCurrent) {
        setHasBeenCurrent(true);
    }

    const endItemsCount = 5;
    const placeholderItems = usePlaceholderItems(endItemsCount);
    const { items: nextItems = null } = useNextDocuments(
        {
            id,
            exclude_id: id,
            type,
            count: endItemsCount,
        },
        {
            enabled: current && id !== null && ended,
        },
    );

    const { items: continueItems = null } = useContinueDocuments(
        {
            id,
            exclude_id: [id, ...(nextItems || []).map(({ id }) => id)],
            type,
            count: endItemsCount - (nextItems !== null ? nextItems.length : 0),
        },
        {
            enabled: nextItems !== null && nextItems.length < endItemsCount,
        },
    );

    const endItems =
        nextItems !== null
            ? [
                  ...(nextItems !== null ? nextItems : []),
                  ...(continueItems !== null ? continueItems : []),
              ]
            : placeholderItems;

    return (
        <div
            className={classNames([
                styles.container,
                {
                    [styles.entered]: entered,
                    [styles.hasPlayed]: hasPlayed,
                    [styles.isTrailer]: isTrailer,
                    [styles.startTimeChoiceOpened]: startTimeChoiceOpened,
                    [styles.ended]: ended,
                },
                className,
            ])}
        >
            {image !== null ? (
                <div className={styles.start}>
                    <Picture
                        media={image}
                        size="large"
                        fetchPriority="high"
                        className={styles.poster}
                    />
                    <PlayButton
                        className={classNames([
                            styles.playButton,
                            {
                                [styles.loading]: willPlay,
                            },
                        ])}
                        loading={willPlay}
                        onClick={onClickPlay}
                        iconClassName={styles.icon}
                    >
                        {isTrailer ? <span className={styles.label}>Bande-Annonce</span> : null}
                    </PlayButton>
                    {startTime !== null && startTime > 0 ? (
                        <div className={styles.startTimeChoice}>
                            <BasicButton className={styles.button} onClick={onClickPlay}>
                                Reprendre à {formatDuration(startTime)}
                            </BasicButton>
                            <BasicButton className={styles.button} onClick={onClickPlayStart}>
                                Jouer du début
                            </BasicButton>
                        </div>
                    ) : null}
                    <div className={styles.metadata}>
                        <div className={styles.label}>
                            {finalSurtitle !== null ? (
                                <Surtitle surtitle={finalSurtitle} className={styles.surtitle} />
                            ) : null}
                            <SummaryTitleTypography className={styles.title}>
                                {title}
                            </SummaryTitleTypography>
                            {blocked ? (
                                <Blocked unblocked={unblocked} className={styles.blocked} />
                            ) : null}
                        </div>
                        {video !== null ? (
                            <Duration duration={duration} timestamp className={styles.duration} />
                        ) : null}
                    </div>
                    <BasicButton className={styles.backdropButton} onClick={onClickPlay} />
                </div>
            ) : null}
            <div className={styles.end}>
                <div className={styles.inner}>
                    <ListTitle className={styles.title}>Poursuivre l’écoute</ListTitle>
                    <div className={styles.lists}>
                        <DocumentsList
                            presentation="grid"
                            cardTheme="rounded"
                            cardSize="medium"
                            // autoColumns
                            columns={1}
                            cardWithout={['time', 'author']}
                            cardWith={['episode', 'brand-icon']}
                            clickRef="video_next"
                            hook="next"
                            withBorder={false}
                            className={styles.next}
                            itemsClassName={styles.items}
                            itemClassName={styles.item}
                            withoutLoader
                            items={
                                endItems !== null && endItems.length > 0 ? endItems.slice(0, 1) : []
                            }
                            loading="lazy"
                        />
                        <DocumentsList
                            presentation="grid"
                            cardType="horizontal"
                            cardTheme="rounded"
                            cardSize="small"
                            cardWithout={['time', 'author']}
                            cardWith={['episode', 'brand-icon']}
                            // autoColumns
                            clickRef="video_continue"
                            hook="next"
                            withBorder={false}
                            cardWidth={300}
                            columns={1}
                            className={styles.others}
                            itemsClassName={styles.items}
                            itemClassName={styles.item}
                            withoutLoader
                            items={
                                endItems !== null && endItems.length > 0 ? endItems.slice(1) : []
                            }
                        />
                    </div>
                </div>
            </div>
            {finalVideo !== null && hasBeenCurrent ? (
                <Video
                    media={finalVideo}
                    className={styles.video}
                    muted={muted}
                    controls={controls}
                    controlsCompact={!current || (!entered && !entering && playing) || leaving}
                    controlsTheme={brandHandle || defaultBrandHandle}
                    playing={playing && !paused && current}
                    autoPlay={autoPlay && current && !paused && !hasAutoPlayed}
                    nativeControls
                    withoutBlock
                    playerRef={playerRef}
                    onPlay={onPlay}
                    onPause={onPause}
                    onMute={onMute}
                    onUnmute={onUnmute}
                    onProgress={onProgress}
                    onBufferStart={onBufferStart}
                    onBufferEnd={onBufferEnd}
                    onEnded={onEnded}
                    onClick={onClickVideo}
                />
            ) : null}
        </div>
    );
}

export default VideoDocument;
